import React, { useEffect, useMemo, useState, useRef} from "react";
import { scrollToTop } from "./landing/wizard/wizardScroll";
import { Link, useOutletContext } from "react-router-dom";
import { getCashLeague } from "../../api/user";
import { SectionError } from "../../components/Skeleton/LoadError";
import CashGameRow from "./landing/CashGameRow";
import CashGameCompletedCard from "./landing/CashGameCompletedCard";
import FeaturedCashCompactCard from "./landing/FeaturedCashCompactCard";
import CashGameUpcomingCard from "./landing/CashGameUpcomingCard";
import CashGameMetaBar from "./landing/CashGameMetaBar";
import { InvitePeopleIcon } from "./landing/icons";
import LeagueEmptyState, { EmptyStateArt } from "./landing/LeagueEmptyState";
import { getLeagueStatus } from "./landing/leagueStatus";
import usePullToRefresh from "../../hooks/usePullToRefresh";
import PullToRefreshIndicator from "../../components/Other/PullToRefreshIndicator";

// Cash Games — every cash game QUSHi has published, in one list.
//
// Reached from "View all" beside Featured Cash Games on League Play, which shows
// only the two most recently published. Before this screen existed that link led
// to the pre-revamp card grid, which also never loaded anything when opened from
// League Play (it only fetched when handed a router `viewMode`, and the View all
// link passed none) — so a third cash game had nowhere to be. That is exactly
// what the client reported: "the third disappears because we do not have the view
// all screens setup for cash game."
//
// Client: "Bring the Cash Games View All pages over from Public Leagues with the
// same Starting Soon, Live Now, and Completed tabs. we are using are exact card
// system for these screens." So this is PublicLeagues' screen, tab for tab and
// container for container, with three differences the client asked for by name:
// the metabar between the subtitle and the tabs, the cash-game rows, and this
// list's own source (every cash game, rather than every non-cash public league).
// Sentence case, matching Public Leagues exactly. The client wrote the tab names
// capitalised in their message ("the same Starting Soon, Live Now, and Completed
// tabs"), but they were naming the tabs Public Leagues already has, and those
// read "Starting soon" / "Live now". Since the instruction is that this screen
// IS that screen, two screens spelling the same three tabs differently is the
// thing to avoid. One word each to flip back if they meant the capitals.
const TABS = [
  { key: "soon", label: "Starting soon" },
  { key: "live", label: "Live now" },
  { key: "done", label: "Completed" },
];

// Client, on the empty states: "The 'starting soon' and 'live now' empty states
// are next to each other. Other than the message, they should be identical. For
// the completed tab empty-state message, use the same message we used for the
// closed public leagues empty state."
//
// The first two therefore differ only in copy, and the third is a verbatim copy
// of PublicLeagues' `done` state — not a new wording that means the same thing.
const EMPTY = {
  soon: {
    title: "No cash games open right now",
    body: "Check back soon.",
  },
  live: {
    title: "Nothing live right now",
    body: "Check Starting soon to see what's coming.",
  },
  done: {
    title: "No finished leagues yet",
    body: "Competitions land here once they wrap.",
  },
};

// The card the client's empty-state design closes the screen with:
//
//   [icon]  More ways to compete
//           Explore Public Leagues and find your next challenge.
//   [        Browse Leagues        ]
//
// It replaces the "Create a Public League" card that came over with my copy of
// the Public Leagues screen — that one was never asked for and pointed a player
// at creating a league, which is not the way out of an empty Cash Games list.
// This is, and it is the client's own copy and destination.
//
// The 56px mark is the design's "Community Plus Icon". That artwork is Stitch's
// own AI render, so the file is asked for first and the app's real
// InvitePeopleIcon (people + a plus — the same subject) stands in until it
// arrives, exactly as the metabar icons were handled.
const MoreWaysIcon = () => {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return (
      <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-[#F8FAFA]">
        <InvitePeopleIcon className="h-7 w-7" stroke="#004C50" />
      </span>
    );
  }
  return (
    <img
      src="/cash-game-more-ways.png"
      alt=""
      loading="lazy"
      decoding="async"
      className="h-14 w-14 shrink-0 object-contain"
      onError={() => setBroken(true)}
    />
  );
};

const MoreWaysCard = () => (
  <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-5">
    <div className="flex items-center gap-4">
      <MoreWaysIcon />
      <div className="min-w-0 flex-1">
        <h2 className="font-montserrat font-bold text-base md:text-lg text-[#0F4146]">
          More ways to compete
        </h2>
        <p className="mt-0.5 text-sm text-secondary">
          Explore Public Leagues and find your next challenge.
        </p>
      </div>
    </div>
    {/* Teal, not the greenXD of the Public Leagues promo: the design draws it
        teal, and teal is what every other action on a cash surface is. */}
    <Link
      to="/contest/public-leagues"
      className="mt-4 flex w-full items-center justify-center rounded-xl bg-[#004C50] py-3.5 font-bold text-base text-white transition-colors hover:bg-[#003437]"
    >
      Browse Leagues
    </Link>
  </div>
);

// One rule for which tab a game belongs in — the same one PublicLeagues uses, so
// a game can't be listed as upcoming and drawn as if it were running. The tab
// key is then handed to the row, which is what picks HOSTED BY / LEADER / WINNER.
const phaseOf = (game) => {
  const tone = getLeagueStatus(game)?.tone;
  if (game?.status === "completed" || tone === "ended") return "done";
  if (tone === "live") return "live";
  return "soon";
};

const startMs = (game) => {
  const t = game?.start_date ? new Date(game.start_date).getTime() : NaN;
  return Number.isNaN(t) ? Infinity : t;
};

const CashGames = () => {
  // Contest.jsx owns the search field in its header band. It is suppressed on
  // this route (see isSelfTitledPage there), so `search` stays empty here — the
  // filter below is kept anyway so the screen behaves correctly if the search
  // bar is ever turned back on for it, exactly as PublicLeagues does.
  const { search } = useOutletContext() || {};
  const [tab, setTab] = useState("soon");
  const [state, setState] = useState({ loading: true, error: false, data: [] });

  const load = async (signal) => {
    const res = await getCashLeague({ signal });
    return res?.data?.data || [];
  };

  const refreshFunction = async () => {
    try {
      setState({ loading: false, error: false, data: await load() });
    } catch (e) {
      console.error("Cash games refresh failed:", e);
      setState((s) => ({ loading: false, error: true, data: s.data }));
    }
  };


  // Client: "When you open the mobile version Cash Games > View All from League
  // Play Home, the page does not always start at the very top where the 'Cash
  // Games' title is visible... Please make sure: All three View All pages open
  // at the top of the page."
  //
  // They did not, and it is the same cause the create-league wizard had: the app
  // does not scroll the WINDOW. `.mainWrapper` is the scroller, so React Router
  // swapping the route leaves that element at whatever offset the PREVIOUS
  // screen was scrolled to — and League Play Home is usually scrolled well down
  // by the time anyone reaches a View All link. scrollToTop walks up to the real
  // scroller instead of calling window.scrollTo, which is a no-op here.
  const topRef = useRef(null);
  useEffect(() => {
    scrollToTop(topRef.current);
  }, []);

  const { containerProps, isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: refreshFunction,
    disabled: false,
  });

  useEffect(() => {
    const controller = new AbortController();
    setState((s) => ({ ...s, loading: true, error: false }));
    load(controller.signal)
      .then((data) => setState({ loading: false, error: false, data }))
      .catch((e) => {
        if (e?.name === "AbortError" || e?.name === "CanceledError") return;
        console.error("Cash games load failed:", e);
        setState({ loading: false, error: true, data: [] });
      });
    return () => controller.abort();
  }, []);

  const searched = useMemo(() => {
    const q = (search || "").trim().toLowerCase();
    if (!q) return state.data;
    return state.data.filter(
      (g) =>
        (g.title && g.title.toLowerCase().includes(q)) ||
        (g.description && g.description.toLowerCase().includes(q))
    );
  }, [state.data, search]);

  const buckets = useMemo(() => {
    const out = { soon: [], live: [], done: [] };
    searched.forEach((g) => {
      const status = getLeagueStatus(g);
      if (!status) return; // cancelled
      out[phaseOf(g)].push(g);
    });
    // Soonest first for what's coming; most recent first for what's finished.
    out.soon.sort((a, b) => startMs(a) - startMs(b));
    out.live.sort((a, b) => startMs(a) - startMs(b));
    out.done.sort((a, b) => startMs(b) - startMs(a));
    return out;
  }, [searched]);

  const rows = buckets[tab];

  // Client, on Completed: "Here is how the 'Completed' Cash Games tab cards
  // will look. Cards for mobile and desktop, not divider lines."
  // Client, on Live now: "I believe you already have this card pretty close.
  // This section will show cards. Not a divider-line layout like public
  // leagues."
  //
  //
  // Live now renders the SAME component as the secondary card on League Play
  // home — "you already have this card" is literally true, and one definition
  // means the card a player sees on home and the card they see after tapping
  // View all cannot drift apart.
  //
  // The shared panel below draws the white card the rows sit inside on desktop.
  // Cards bring their own, so for those tabs the panel's chrome comes off
  // rather than putting cards inside a card — but only when there ARE cards:
  // the empty state, the error and the skeleton stay in the panel so the screen
  // does not change shape as you move between tabs with nothing in them.
  // All three tabs are cards now — Starting soon was the last to move:
  // "For the cash games 'Starting soon' screen, we will also use a card instead
  // of the divider line."
  const CARD_TABS = {
    soon: CashGameUpcomingCard,
    live: FeaturedCashCompactCard,
    done: CashGameCompletedCard,
  };
  const CardComponent = CARD_TABS[tab] || null;
  const asCards =
    !!CardComponent && !state.loading && !state.error && rows.length > 0;

  return (
    <>
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} />
      <div className="pb-24 md:pb-16" {...containerProps}>
        <div ref={topRef} className="mx-auto max-w-screen-md">
          {/* Screen title and subtitle, at the token level the client asked for:
              the same `text-pageTitle` Montserrat heading and secondary subtitle
              every other League Play sub-page uses, so Cash Games does not
              introduce a third heading treatment. */}
          <div className="px-4 md:px-8">
            <h1 className="font-montserrat font-bold text-title-subpage md:text-title-page text-pageTitle">
              Cash Games
            </h1>
            <p className="mt-1 text-sm md:text-base text-secondary">
              Trade with virtual cash. Compete for real cash.
            </p>
          </div>

          {/* "placement is between the subtitle and the Starting soon, Live now,
              and Completed tabs." Full-bleed on mobile because the design's
              hairlines run edge to edge; inset on desktop where the content
              column has margins of its own. */}
          <CashGameMetaBar className="mt-4 md:mx-8 md:rounded-xl md:border" />

          <div className="px-4 md:px-8">
            {/* Same tab treatment as Public Leagues — thirds on mobile, a compact
                left-aligned row with a 48px gap on desktop — because the client
                asked for this screen to be that screen. */}
            <div
              role="tablist"
              className="mt-5 grid grid-cols-3 border-b border-divider md:flex md:items-center md:gap-12"
            >
              {TABS.map((t) => {
                const active = t.key === tab;
                return (
                  <button
                    key={t.key}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.key)}
                    className={`-mb-px w-full border-b-2 pb-3 text-center text-sm transition-colors md:w-auto md:shrink-0 md:text-left md:text-base ${
                      active
                        ? "border-greenXD font-semibold text-greenXD"
                        : "border-transparent text-secondary hover:text-[#0F172A]"
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {/* One container whatever the tab is showing — rows, the empty state,
                an error or the skeleton — so the card never changes shape between
                tabs. The client's Rev 4 instruction for Public Leagues, and this
                screen is that screen. */}
            <div
              className={
                asCards
                  ? "mt-4"
                  : "mt-4 md:rounded-2xl md:border md:border-gray-200 md:bg-white md:px-5"
              }
            >
              {state.loading ? (
                <div className="divide-y divide-divider">
                  {[0, 1, 2].map((i) => (
                    <div key={i} className="space-y-2 py-4">
                      <div className="h-4 w-28 animate-pulse rounded bg-[#F3F4F6]" />
                      <div className="h-5 w-3/4 animate-pulse rounded bg-[#F3F4F6]" />
                      <div className="h-4 w-1/2 animate-pulse rounded bg-[#F3F4F6]" />
                    </div>
                  ))}
                </div>
              ) : state.error ? (
                <div className="py-4">
                  <SectionError
                    message="Cash games could not be loaded"
                    onRetry={() => window.location.reload()}
                  />
                </div>
              ) : rows.length === 0 ? (
                <div className="py-2 md:py-4">
                  {search ? (
                    <p className="py-10 text-center text-sm text-secondary">
                      No cash games match “{search}”.
                    </p>
                  ) : (
                    /* "The different tabs should use the same empty state image
                       (size)." One `variant` for all three, so the art cannot
                       change size or subject between tabs. */
                    <LeagueEmptyState
                      art={<EmptyStateArt variant="cashGames" />}
                      title={EMPTY[tab].title}
                      body={EMPTY[tab].body}
                    />
                  )}
                </div>
              ) : asCards ? (
                <div className="space-y-3 md:space-y-4">
                  {rows.map((game) => (
                    <CardComponent key={game.id} game={game} />
                  ))}
                </div>
              ) : (
                <div className="divide-y divide-divider">
                  {rows.map((game) => (
                    <CashGameRow key={game.id} game={game} phase={tab} />
                  ))}
                </div>
              )}
            </div>

            <MoreWaysCard />
          </div>
        </div>
      </div>
    </>
  );
};

export default CashGames;
